import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import NotFound from "shared/components/UIElements/NotFound";
import MapSinglePoint from "components/shared/MapSinglePoint";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import LoginCodeField from "features/users/components/LoginCodeField";
import { AuthContext } from "shared/context/auth-context";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";

import InputPhoneNumber from "components/ui/input/InputPhoneNumber";
import { getCurrentLanguage } from "shared/utility/lang";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { USER_TYPES, SCHOOL_TYPES } from "@routebot/constants";

const DriverItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  //standard code
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // context
  const { drivers, schools } = useContext(StoreContext);

  const driverSchoolOptions = schools
    .filter((school) => school.type === SCHOOL_TYPES.SCHOOL)
    .map((school) => ({
      label: school.name,
      value: school.id.toString(),
    }));

  // item info
  const itemType = "user";
  const itemTypeText = "driverItem";
  const endPointUpdate = "users";
  const endPointDelete = "users";
  const endPointCreate = "users";

  // item special states
  const [markerLocation, setMarkerLocation] = useState();
  const [userAddress, setUserAddress] = useState();
  const [addressInSync, setAddressInSync] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState(() => auth?.countryCode || "TR");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

  // item special functions
  const mapDataChangeHandler = (address, location, inSync) => {
    setMarkerLocation(location);
    setUserAddress(address);
    setAddressInSync(inSync);
    inputHandler("address", address, true);
  };

  const phoneNumberChangeHandler = (phoneNumber, countryCode, isValid) => {
    setPhoneNumber(phoneNumber || "");
    setCountryCode(countryCode);
    setIsPhoneNumberValid(isValid);
  };

  // formstates
  const [formState, inputHandler] = useForm(
    {
      //inputs
      name: { value: "", isValid: true },
      plate: { value: "", isValid: true },
      email: { value: "", isValid: true },
      address: { value: null, isValid: true },
      identity: { value: "", isValid: true },
      school: { value: driverSchoolOptions.length === 1 ? driverSchoolOptions[0].value : null, isValid: true },
    },
    true,
  );

  // formstates fetch
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentUser = drivers.find((user) => user.id === itemId);
      setLoadedItem(currentUser);
      if (currentUser?.location && currentUser?.location.lat && currentUser?.location.lng) {
        setMarkerLocation(currentUser?.location);
        setAddressInSync(true);
      } else {
        setMarkerLocation(null);
        setAddressInSync(false);
      }
      setPhoneNumber(currentUser?.phonenumber || "");
      setCountryCode(currentUser?.countryCode || auth?.countryCode || "TR");
      setUserAddress(currentUser?.address);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [drivers, itemId]);

  // standard code
  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // item update
  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        name: formState.inputs.name.value.toString().trim(),
        phonenumber: phoneNumber,
        countryCode: countryCode,
        plate: formState.inputs.plate.value.toString().trim(),
        usertype: USER_TYPES.DRIVER,
        email: formState.inputs.email.value,
        address: userAddress,
        identity: formState.inputs.identity.value,
      };

      // Sadece useSpecialDriver true ise school bilgisini gonder
      if (auth.company?.useSpecialDriver && formState.inputs.school.value) {
        infoToBeSent.school = formState.inputs.school.value;
      }

      if (markerLocation) {
        infoToBeSent.location = { lat: markerLocation?.lat || 1.1, lng: markerLocation?.lng || 1.1 };
      }

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTypeText}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTypeText}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          {auth.company?.useSpecialDriver && (
            <CustomSelect
              options={driverSchoolOptions}
              id="school"
              onInput={inputHandler}
              label={t("school") + " *"}
              initialValue={
                driverSchoolOptions.find((option) => option.value.toString() === loadedItem?.school?.toString())
                  ? driverSchoolOptions.find((option) => option.value.toString() === loadedItem?.school?.toString())
                  : driverSchoolOptions.length === 1
                    ? driverSchoolOptions[0]
                    : null
              }
              initialValid={isNewItem ? false : true}
            />
          )}

          <div className="horizontal-flex">
            <Input
              id="name"
              element="input"
              type="text"
              label={t("name") + " *"}
              validators={[VALIDATOR_REQUIRE()]}
              errorText={t("requireField", { item: t("name") })}
              initialValue={loadedItem?.name}
              initialValid={isNewItem ? false : true}
              onInput={inputHandler}
            />
          </div>

          <div className="horizontal-flex">
            <InputPhoneNumber
              id="phonenumber"
              label={t("phone")}
              phoneNumber={phoneNumber}
              countryCode={countryCode}
              onChange={phoneNumberChangeHandler}
              errorText={t("phoneNumberorEmpty")}
              language={getCurrentLanguage()}
              hideLabel={auth.countryCode === countryCode}
            />

            <Input
              id="plate"
              element="input"
              type="text"
              label={t("plate")}
              validators={[VALIDATOR_NONE()]}
              initialValue={loadedItem?.plate || ""}
              initialValid={true}
              onInput={inputHandler}
            />
          </div>

          <div className="horizontal-flex">
            <Input
              id="identity"
              element="input"
              type="text"
              label={t("identityNumber")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("requireField", { item: t("identityNumber") })}
              initialValue={loadedItem?.identity || ""}
              initialValid={true}
              onInput={inputHandler}
            />

            <Input
              id="email"
              element="input"
              type="text"
              label={t("secondPhone")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("requireField", { item: t("email") })}
              onInput={inputHandler}
              initialValue={loadedItem?.email || ""}
              initialValid={true}
            />
            {!isNewItem && (
              <LoginCodeField loginCode={loadedItem?.loginCode} phoneNumber={phoneNumber} countryCode={countryCode} />
            )}
          </div>

          <MapSinglePoint
            id="userItemMap"
            label={formState.inputs.name.value}
            initialAddress={userAddress || ""}
            initialAddressInSync={addressInSync}
            initialLocation={markerLocation}
            onInput={inputHandler}
            isRequired={false}
            onMapDataChange={mapDataChangeHandler}
          ></MapSinglePoint>

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={
              !formState.isValid ||
              !isPhoneNumberValid ||
              isLoading ||
              (auth.company?.useSpecialDriver && !formState.inputs.school.value)
            }
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default DriverItem;
